import * as React from 'react';
import { createFileRoute, Link, Outlet, useMatches, useNavigate, useRouterState } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ImageOff, ArrowDownUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as Dialog from '@/components/ui/dialog';
import { Combobox } from '@/components/combobox';
import { FilterInput } from '@/components/filter-input';
import { SearchBox } from '@/components/search-box';
import { useStore } from '@/lib/store-context';
import {
  getStock, getMasters, getItemDetail, getStockBreakdown,
  type StockRow, type ItemDetail, type MatchMode, type SizeGroup, type StockBreakdownResponse,
} from '@/lib/inventory-api';
import { fmtINR, fmtNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

// Filter state lives in the URL so reports are shareable / refresh-safe.
const Search$ = z.object({
  brand: z.string().optional(),
  style: z.string().optional(),
  color: z.string().optional(),
  subgroup: z.string().optional(),
  item: z.string().optional(),
  q: z.string().optional(),
  match: z.enum(['contains', 'startswith', 'similar']).optional(),
  sizeGroup: z.string().optional(),   // SIZEGRPCODE from the POS master
  open: z.string().optional(), // "{item}|{brand}|{style}|{color}" of the row in the drawer
  // Movement-tab filters
  category: z.enum(['all', 'fast', 'slow', 'dead', 'new']).optional(),
  fastDays: z.coerce.number().optional(),
  deadDays: z.coerce.number().optional(),
  // Transfers-tab filters
  from: z.string().optional(),
  to: z.string().optional(),
  direction: z.enum(['all', 'in', 'out']).optional(),
  page: z.coerce.number().optional(),
});

export const Route = createFileRoute('/_app/inventory')({
  validateSearch: Search$,
  component: InventoryLayout,
});

function InventoryLayout() {
  const linkCls = 'rounded-md px-3 py-1.5 text-sm transition-colors';
  const activeCls = 'bg-muted text-foreground';
  const matches = useMatches();
  const onChild = matches.some((m) => m.routeId.startsWith('/_app/inventory/'));

  return (
    <div className="space-y-4">
      <nav className="flex items-center gap-2 border-b pb-2">
        <Link 
          to="/inventory" 
          search={{}} 
          className={cn(linkCls, 'text-muted-foreground hover:text-foreground')} 
          activeProps={{ className: cn(linkCls, activeCls) }}
          activeOptions={{ exact: true }}
        >
          Stock Ledger
        </Link>
        <Link 
          to="/inventory/movement" 
          search={{}} 
          className={cn(linkCls, 'text-muted-foreground hover:text-foreground')} 
          activeProps={{ className: cn(linkCls, activeCls) }}
        >
          Movement
        </Link>
        <Link 
          to="/inventory/transfers" 
          search={{}} 
          className={cn(linkCls, 'text-muted-foreground hover:text-foreground')} 
          activeProps={{ className: cn(linkCls, activeCls) }}
        >
          Transfers
        </Link>
      </nav>
      {onChild ? <Outlet /> : <StockLedger />}
    </div>
  );
}

function StockLedger() {
  const search = Route.useSearch();
  const nav = Route.useNavigate();
  const { selected: selectedStore } = useStore();

  const filters = {
    brand: search.brand ?? null,
    style: search.style ?? null,
    color: search.color ?? null,
    subgroup: search.subgroup ?? null,
    item: search.item ?? null,
    q: search.q ?? null,
    match: (search.match ?? 'contains') as MatchMode,
    sizeGroup: (search.sizeGroup ?? null) as SizeGroup | null,
  };

  const setF = (patch: Partial<typeof filters>) => {
    nav({
      to: '.',
      search: (prev) => {
        const next = { ...(prev as Record<string, unknown>) };
        for (const [k, v] of Object.entries(patch)) {
          if (k === 'match') {
            // 'contains' is the default — keep URL clean by omitting it.
            if (!v || v === 'contains') delete next[k];
            else next[k] = v;
          } else if (k === 'sizeGroup') {
            if (!v) delete next[k];
            else next[k] = v;
          } else if (v == null || v === '') {
            delete next[k];
          } else {
            next[k] = v;
          }
        }
        return next;
      },
      replace: true,
    });
  };

  const mastersQ = useQuery({
    queryKey: ['inventory:masters'],
    queryFn: getMasters,
    staleTime: 5 * 60_000,
  });
  // Server cap. Default keeps the response under ~3 MB; user can lift it.
  const [limit, setLimit] = React.useState(500);
  React.useEffect(() => { setLimit(500); }, [filters.brand, filters.style, filters.color, filters.subgroup, filters.item, filters.q, filters.sizeGroup]);

  // Don't run an unfiltered query — without ANY filter the result is
  // 38k rows × 80+ size columns and the browser dies. Require the user
  // to pick at least one dimension.
  const hasFilter = !!(filters.brand || filters.style || filters.color || filters.subgroup || filters.item || filters.q || filters.sizeGroup);

  // Multi-store aware: when "All stores" is selected (or nothing), the API
  // returns cells for every accessible store. When a specific store is
  // picked, we pass `stores: [store]` to restrict to that DB. The store-
  // selector's "All stores" option therefore actually has teeth here.
  const storeFilter: string[] | null =
    !selectedStore || selectedStore === 'all' ? null : [selectedStore];
  const stockQ = useQuery({
    queryKey: ['inventory:stock', filters, limit, storeFilter],
    queryFn: () => getStock({
      brand: filters.brand, style: filters.style, color: filters.color,
      subgroup: filters.subgroup, item: filters.item, q: filters.q,
      match: filters.match,
      sizeGroup: filters.sizeGroup,
      limit,
      stores: storeFilter,
    }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: hasFilter,
  });

  const openKey = search.open ?? null;
  const openRow = openKey
    ? (stockQ.data?.rows ?? []).find((r) => keyOf(r) === openKey) ?? null
    : null;

  const [sizeBreak, setSizeBreak] = React.useState<{ row: StockRow; size: string } | null>(null);

  const activeFilterCount = [filters.brand, filters.style, filters.color, filters.subgroup, filters.item, filters.q, filters.sizeGroup]
    .filter(Boolean).length;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Daleep · Inventory</p>
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">Stock Ledger</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live stock across {stockQ.data?.stores.length ?? '—'} store{(stockQ.data?.stores.length ?? 0) === 1 ? '' : 's'}
            {stockQ.data && <> · {fmtNumber(stockQ.data.totalRows)} variant{stockQ.data.totalRows === 1 ? '' : 's'}</>}
          </p>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-3 pt-4">
          <div className="flex flex-wrap items-end gap-2">
            <Combobox
              label="Sub-group"
              value={filters.subgroup ?? ''}
              options={mastersQ.data?.subgroup ?? []}
              onChange={(v) => setF({ subgroup: v || null })}
              placeholder="All sub-groups"
              className="min-w-[160px]"
            />
            <div className="min-w-[160px]">
              <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Size group
              </label>
              <select
                value={filters.sizeGroup ?? ''}
                onChange={(e) => setF({ sizeGroup: (e.target.value || null) as SizeGroup | null })}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All sizes</option>
                {(mastersQ.data?.sizeGroups ?? []).map((o) => (
                  <option key={o.code} value={o.code} title={o.sizes.join(', ')}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <FilterInput
              label="Brand"
              value={filters.brand ?? ''}
              matchMode={filters.match}
              onChange={(v) => setF({ brand: v || null })}
              placeholder="All brands · type to filter"
              className="min-w-[200px]"
            />
            <FilterInput
              label="Article"
              value={filters.style ?? ''}
              matchMode={filters.match}
              onChange={(v) => setF({ style: v || null })}
              placeholder="All articles · type to filter"
              className="min-w-[220px]"
            />
            <FilterInput
              label="Color"
              value={filters.color ?? ''}
              matchMode={filters.match}
              onChange={(v) => setF({ color: v || null })}
              placeholder="All colors · type to filter"
              className="min-w-[200px]"
            />
            <FilterInput
              label="Item"
              value={filters.item ?? ''}
              matchMode={filters.match}
              onChange={(v) => setF({ item: v || null })}
              placeholder="All items · type to filter"
              className="min-w-[200px]"
            />
            <SearchBox
              q={filters.q}
              onQChange={(q) => nav({ to: '.', search: (prev) => ({ ...prev, q }), replace: true })}
              match={filters.match}
              onMatchChange={(m) => setF({ match: m })}
            />
            {activeFilterCount > 0 && (
              <Button
                variant="ghost" size="sm"
                onClick={() => setF({ brand: null, style: null, color: null, subgroup: null, item: null, q: null, sizeGroup: null })}
              >
                Clear ({activeFilterCount})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Stock by size · {stockQ.data?.stores.join(' · ') ?? '—'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!hasFilter && (
            <div className="rounded-md border border-dashed p-8 text-center">
              <p className="text-sm font-medium">Pick a sub-group, brand, article, or item to view stock.</p>
              <p className="mt-1 text-xs text-muted-foreground">
                The full corpus is ~38,000 variants across 80+ sizes — too much to render at once. Filters keep it snappy.
              </p>
            </div>
          )}
          {hasFilter && stockQ.isLoading && !stockQ.data && <p className="text-sm text-muted-foreground">Loading…</p>}
          {hasFilter && stockQ.error && <p className="text-sm text-destructive">{(stockQ.error as Error).message}</p>}
          {hasFilter && stockQ.data && stockQ.data.rows.length === 0 && (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No matching stock. Try a wider size group or a different search.
            </p>
          )}
          {hasFilter && stockQ.data && stockQ.data.rows.length > 0 && (
            <>
              {stockQ.data.truncated && (
                <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  Showing first <strong>{fmtNumber(stockQ.data.rows.length)}</strong> of <strong>{fmtNumber(stockQ.data.totalRows)}</strong> variants. Narrow filters to see specific stock, or click "Show more" below.
                </p>
              )}
              {/*
                Sticky header needs the scroll container to be on both axes
                — overflow-x-auto alone creates an x-only sticky frame and
                position:sticky has nothing to bind to vertically. Cap the
                table viewport at 70vh and let it scroll inside; the sticky
                row then pins to the top of that viewport.
              */}
              <div className={cn('max-h-[70vh] overflow-auto rounded-md border', stockQ.isFetching && 'opacity-60 transition-opacity')}>
                <PivotTable
                  stores={stockQ.data.stores}
                  sizes={stockQ.data.sizes}
                  rows={stockQ.data.rows}
                  onOpen={(r) => nav({ to: '.', search: (prev) => ({ ...prev, open: keyOf(r) }), replace: true })}
                  openKey={openKey}
                  onSizeOpen={(r, size) => setSizeBreak({ row: r, size })}
                />
              </div>
              {stockQ.data.truncated && (
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={() => setLimit((l) => l + 1000)}>
                    Show 1000 more
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog.Root open={!!openRow} onOpenChange={(v) => { if (!v) nav({ to: '.', search: (prev) => ({ ...prev, open: undefined }), replace: true }); }}>
        {openRow && (
          <Dialog.Content className="max-h-[90vh] w-full max-w-3xl overflow-y-auto sm:max-w-3xl">
            <ItemDrawer row={openRow} stores={stockQ.data?.stores ?? []} sizes={stockQ.data?.sizes ?? []} selectedStore={selectedStore} />
          </Dialog.Content>
        )}
      </Dialog.Root>

      <Dialog.Root open={!!sizeBreak} onOpenChange={(v) => { if (!v) setSizeBreak(null); }}>
        {sizeBreak && (
          <Dialog.Content className="max-h-[90vh] w-full max-w-3xl overflow-y-auto sm:max-w-3xl">
            <StockBreakdownPopup
              row={sizeBreak.row}
              store={selectedStore}
              size={sizeBreak.size}
              onClose={() => setSizeBreak(null)}
            />
          </Dialog.Content>
        )}
      </Dialog.Root>
    </div>
  );
}

function keyOf(r: StockRow): string {
  // storeDb is part of the key in multi-store mode: the same variant now gets
  // one row per store, so without it both rows share a React key and the
  // drawer's open-row match hits whichever came first.
  return (r.storeDb ? [r.storeDb, r.item, r.brand, r.style, r.color]
                    : [r.item, r.brand, r.style, r.color]).join('|');
}

// ────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────
// Pivot table — multi-store cells. Each cell shows total qty across
// stores; on hover a small breakdown by store appears. Rows tinted red
// when zero, amber when one store is empty (transfer signal).
// ────────────────────────────────────────────────────────────
function PivotTable({
  stores, sizes, rows, onOpen, openKey, onSizeOpen,
}: {
  stores: string[];
  sizes: string[];
  rows: StockRow[];
  onOpen: (r: StockRow) => void;
  openKey: string | null;
  onSizeOpen: (r: StockRow, size: string) => void;
}) {
  // Sticky header: position:sticky must live on the <th>s, not <thead>
  // (Safari + some other browsers don't honor sticky on thead/tr). Each
  // header cell gets its own background-color so the row beneath it
  // doesn't bleed through when scrolling.
  const stickyTh = 'sticky top-0 z-10 bg-sand shadow-[0_1px_0_0_hsl(var(--border))]';
  // With several stores selected the API emits one row per store rather than
  // summing them, so name the owning store.
  const showStore = stores.length > 1;
  return (
    <table className="w-full text-[12px]">
      <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
        <tr>
          <Th className={cn(stickyTh, 'w-10')}>#</Th>
          {showStore && <Th className={stickyTh}>Store</Th>}
          <Th className={stickyTh}>Item</Th>
          <Th className={stickyTh}>Brand</Th>
          <Th className={stickyTh}>Article</Th>
          <Th className={stickyTh}>Color</Th>
          {sizes.map((s) => <Th key={s} align="right" className={stickyTh}>{s}</Th>)}
          <Th align="right" className={stickyTh}>Total</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => {
          const k = keyOf(r);
          const isOpen = k === openKey;
          const isZero = r.total === 0;
          return (
            <tr
              key={k}
              className={cn(
                'cursor-pointer border-t border-border/40 transition-colors hover:bg-sand/40',
                isZero && 'bg-rose-soft/40 hover:bg-rose-soft/60',
                isOpen && 'bg-copper-soft/40 hover:bg-copper-soft/50',
              )}
              tabIndex={0}
              role="button"
              aria-label={`Open ${r.brand} ${r.style} ${r.color}`}
              onClick={() => onOpen(r)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(r); } }}
            >
              <Td className="text-muted-foreground font-mono-num">{idx + 1}</Td>
              {showStore && (
                <Td className="font-medium text-muted-foreground">{shortStore(r.storeDb ?? '')}</Td>
              )}
              <Td>{r.item}</Td>
              <Td className="font-medium">{r.brand}</Td>
              <Td>{r.style}</Td>
              <Td>{r.color}</Td>
              {sizes.map((s) => <PivotCell key={s} size={s} row={r} stores={stores} onSizeOpen={onSizeOpen} />)}
              <Td align="right" className="font-mono-num font-semibold">{fmtNumber(r.total)}</Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PivotCell({ size, row, stores, onSizeOpen }: {
  size: string; row: StockRow; stores: string[];
  onSizeOpen: (r: StockRow, size: string) => void;
}) {
  const cell = row.byStore[size];
  const total = cell ? stores.reduce((s, st) => s + (cell[st] ?? 0), 0) : 0;

  // Zero / no-stock sizes are still clickable so the breakdown (which nets to 0)
  // can be inspected — shown as "0", dimmed.
  const isZero = total === 0;
  const presentStores = cell ? stores.filter((s) => (cell[s] ?? 0) > 0) : [];
  const oneStoreOnly = stores.length > 1 && presentStores.length === 1;
  const tooltip = cell
    ? stores.map((s) => `${shortStore(s)}: ${cell[s] ?? 0}`).join(' · ')
    : 'No stock';

  return (
    <Td align="right" className={cn('font-mono-num', oneStoreOnly && 'bg-amber-100/70 text-amber-900', isZero && 'text-muted-foreground/40')}>
      <button
        type="button"
        title={`${tooltip} — click for ${size} breakdown`}
        className="underline-offset-2 hover:underline"
        onClick={(e) => { e.stopPropagation(); onSizeOpen(row, size); }}
      >
        {total}
      </button>
    </Td>
  );
}

// ────────────────────────────────────────────────────────────
// Drawer
// ────────────────────────────────────────────────────────────
function ItemDrawer({
  row, stores, sizes, selectedStore,
}: {
  row: StockRow; stores: string[]; sizes: string[]; selectedStore: string | null;
}) {
  const [showBreakdown, setShowBreakdown] = React.useState(false);
  
  const detailQ = useQuery({
    queryKey: ['inventory:detail', row.item, row.brand, row.style, row.color, selectedStore ?? null],
    queryFn: () => getItemDetail({ item: row.item, brand: row.brand, style: row.style, color: row.color, store: selectedStore ?? undefined }),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="space-y-4">
      <Dialog.Title className="text-xl font-display font-semibold">
        {row.brand} · {row.style} · <span className="text-muted-foreground">{row.color}</span>
      </Dialog.Title>
      <p className="text-sm text-muted-foreground">{row.item} · {row.subgroup}</p>

      <div className="grid gap-4 md:grid-cols-[200px_1fr]">
        <ImageProbe candidates={detailQ.data?.imageUrls ?? []} />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">MRP</div>
              <div className="font-mono-num">{fmtINR(row.mrp)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">SP</div>
              <div className="font-mono-num font-semibold text-ink">{fmtINR(row.sp)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total in stock</div>
              <div className="font-mono-num text-lg font-semibold">{fmtNumber(row.total)}</div>
            </div>
            <div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowBreakdown(true)}
                className="mt-2"
              >
                <ArrowDownUp className="mr-1.5 h-3.5 w-3.5" />
                Stock Breakdown
              </Button>
            </div>
          </div>

          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Stock by store</div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left">Store</th>
                  {sizes.map((s) => <th key={s} className="text-right">{s}</th>)}
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((st) => {
                  const total = sizes.reduce((sum, s) => sum + (row.byStore[s]?.[st] ?? 0), 0);
                  return (
                    <tr key={st} className="border-t">
                      <td className="py-1 font-mono">{shortStore(st)}</td>
                      {sizes.map((s) => (
                        <td key={s} className={cn('py-1 text-right font-mono-num', (row.byStore[s]?.[st] ?? 0) === 0 && 'text-muted-foreground/40')}>
                          {row.byStore[s]?.[st] ?? 0}
                        </td>
                      ))}
                      <td className="py-1 text-right font-mono-num font-semibold">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Sample barcodes (newest first)</div>
        {detailQ.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
        {detailQ.data && (
          <BarcodeList detail={detailQ.data} />
        )}
      </div>

      {/* Stock Breakdown Popup */}
      <Dialog.Root open={showBreakdown} onOpenChange={setShowBreakdown}>
        <Dialog.Content className="max-h-[85vh] w-full max-w-4xl overflow-y-auto">
          <StockBreakdownPopup row={row} store={selectedStore} onClose={() => setShowBreakdown(false)} />
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Stock Breakdown Popup — shows all in/out transactions
// ────────────────────────────────────────────────────────────
function StockBreakdownPopup({ row, store, size, onClose }: { row: StockRow; store: string | null; size?: string; onClose: () => void }) {
  const breakdownQ = useQuery({
    queryKey: ['inventory:breakdown', row.item, row.brand, row.style, row.color, store, size ?? null],
    queryFn: () => getStockBreakdown({
      item: row.item, brand: row.brand, style: row.style, color: row.color,
      store: store ?? undefined, size,
    }),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <Dialog.Title className="text-lg font-display font-semibold">
        Stock Breakdown: {row.brand} · {row.style} · {row.color}{size ? <> · <span className="text-muted-foreground">Size {size}</span></> : null}
      </Dialog.Title>
      
      {breakdownQ.isLoading && (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading transactions…</div>
      )}
      
      {breakdownQ.error && (
        <div className="py-8 text-center text-sm text-destructive">
          Error loading breakdown: {(breakdownQ.error as Error).message}
        </div>
      )}
      
      {breakdownQ.data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border bg-emerald-50 p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700">Total In</div>
              <div className="font-mono-num text-2xl font-bold text-emerald-700">
                +{fmtNumber(breakdownQ.data.summary.totalIn)}
              </div>
            </div>
            <div className="rounded-lg border bg-rose-50 p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-rose-700">Total Out</div>
              <div className="font-mono-num text-2xl font-bold text-rose-700">
                −{fmtNumber(breakdownQ.data.summary.totalOut)}
              </div>
            </div>
            <div className="rounded-lg border bg-blue-50 p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Net Stock</div>
              <div className="font-mono-num text-2xl font-bold text-blue-700">
                {fmtNumber(breakdownQ.data.summary.netStock)}
              </div>
            </div>
          </div>

          {/* Transactions table */}
          <div className="max-h-[50vh] overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sand text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Size</th>
                  <th className="px-3 py-2 text-left">Barcode</th>
                  <th className="px-3 py-2 text-left">Bill No</th>
                  <th className="px-3 py-2 text-left">Store</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {breakdownQ.data.perStore.flatMap((ps) =>
                  ps.transactions.map((t, i) => (
                    <tr 
                      key={`${ps.storeDb}-${t.barcode}-${t.source}-${i}`}
                      className={cn(
                        'border-t',
                        t.direction === 'in' ? 'bg-emerald-50/50' : 'bg-rose-50/50'
                      )}
                    >
                      <td className="px-3 py-1.5 font-mono-num whitespace-nowrap">
                        {t.date || '—'}
                        {t.time && <span className="ml-1 text-muted-foreground">{t.time}</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={cn(
                          'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium',
                          t.direction === 'in' 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : 'bg-rose-100 text-rose-800'
                        )}>
                          {t.direction === 'in' ? '↓' : '↑'} {t.source}
                          {t.counterpartStore ? (t.direction === 'out' ? ` → ${shortStore(t.counterpartStore)}` : ` ← ${shortStore(t.counterpartStore)}`) : ''}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">{t.size}</td>
                      <td className="px-3 py-1.5 font-mono">{t.barcode}</td>
                      <td className="px-3 py-1.5 font-mono-num">{t.billNo || '—'}</td>
                      <td className="px-3 py-1.5 font-mono">{shortStore(ps.storeDb)}</td>
                      <td className={cn(
                        'px-3 py-1.5 text-right font-mono-num font-semibold',
                        t.direction === 'in' ? 'text-emerald-700' : 'text-rose-700'
                      )}>
                        {t.direction === 'in' ? '+' : '−'}{t.qty}
                      </td>
                    </tr>
                  ))
                )}
                {breakdownQ.data.perStore.every((ps) => ps.transactions.length === 0) && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                      No transactions found for this item.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer with totals breakdown */}
          <div className="flex items-center justify-between border-t pt-3 text-xs">
            <div className="text-muted-foreground">
              <span className="font-medium text-emerald-700">Purchase + Transfer In + Sales Return</span> = In ·
              <span className="font-medium text-rose-700 ml-2">Sale + Transfer Out + Purchase Return</span> = Out
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </>
      )}
    </div>
  );
}

function BarcodeList({ detail }: { detail: ItemDetail }) {
  // Only stickers still in stock are returned, already ordered by real size
  // (SML first, then ascending — see api lib/sizes.ts), grouped per store. Keep
  // that order: the previous purchasedOn-DESC re-sort threw it away, which is
  // why the list read as an arbitrary jumble of sizes.
  const flat: Array<{ store: string; size: string; barcode: string; purchasedOn: string }> = [];
  for (const ps of detail.perStore) {
    for (const s of ps.stickers) {
      flat.push({ store: ps.storeDb, size: s.size, barcode: s.barcode, purchasedOn: s.purchasedOn });
    }
  }
  const visible = flat.slice(0, 24);
  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((s, i) => (
        <span key={`${s.barcode}-${i}`} className="rounded border bg-background px-1.5 py-0.5 font-mono-num text-[11px]">
          <span className="text-muted-foreground">{shortStore(s.store)}·{s.size}</span> {s.barcode}
        </span>
      ))}
      {flat.length > visible.length && (
        <span className="text-xs text-muted-foreground">+{flat.length - visible.length} more</span>
      )}
      {flat.length === 0 && <span className="text-xs text-muted-foreground">No stickers in stock.</span>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// ImageProbe — render the first of the server-verified image URLs. The API
// already HEAD-checked existence, so the first URL loads immediately; the
// onError advance is kept only as a defensive fallback (e.g. a file removed
// after verification / cached).
// ────────────────────────────────────────────────────────────
function ImageProbe({ candidates }: { candidates: string[] }) {
  const [idx, setIdx] = React.useState(0);
  const [done, setDone] = React.useState(false);
  const [gallery, setGallery] = React.useState(false);

  React.useEffect(() => { setIdx(0); setDone(false); }, [candidates.join('|')]);

  if (candidates.length === 0 || done) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-dashed bg-muted text-muted-foreground">
        <div className="text-center text-xs">
          <ImageOff className="mx-auto mb-1 h-6 w-6 opacity-50" />
          No image
        </div>
      </div>
    );
  }
  const url = candidates[idx];
  if (!url) return null;
  const more = candidates.length - 1;
  return (
    <>
      <button
        type="button"
        onClick={() => setGallery(true)}
        title={more > 0 ? `Show all ${candidates.length} images` : 'Show image'}
        aria-label="Show all product images"
        className="group relative block w-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <img
          key={url}
          src={url}
          alt="Product"
          className="aspect-square w-full rounded-lg border bg-background object-contain p-2 transition-opacity group-hover:opacity-90"
          onError={() => {
            if (idx + 1 < candidates.length) setIdx(idx + 1);
            else setDone(true);
          }}
        />
        {more > 0 && (
          <span className="absolute bottom-2 right-2 rounded bg-ink/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
            +{more}
          </span>
        )}
      </button>
      <ImageGallery open={gallery} onOpenChange={setGallery} candidates={candidates} />
    </>
  );
}

/**
 * Every image the vendor lists for this product. Candidates that 404 are
 * dropped as they fail to load rather than filtered up front, so a broken
 * filename costs one hidden <img> instead of a HEAD request per URL.
 */
function ImageGallery({ open, onOpenChange, candidates }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  candidates: string[];
}) {
  const [broken, setBroken] = React.useState<Set<string>>(new Set());
  React.useEffect(() => { setBroken(new Set()); }, [candidates.join('|')]);
  const usable = candidates.filter((c) => !broken.has(c));

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-h-[90vh] w-full max-w-4xl overflow-y-auto">
        <Dialog.Title className="font-display text-lg font-semibold">
          Product images
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            {usable.length} of {candidates.length}
          </span>
        </Dialog.Title>
        {usable.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No images loaded.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-3">
            {usable.map((u) => (
              <a key={u} href={u} target="_blank" rel="noreferrer" className="block">
                <img
                  src={u}
                  alt="Product"
                  loading="lazy"
                  className="aspect-square w-full rounded-lg border bg-background object-contain p-2"
                  onError={() => setBroken((prev) => new Set(prev).add(u))}
                />
              </a>
            ))}
          </div>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ────────────────────────────────────────────────────────────
function shortStore(db: string): string {
  // Friendly short labels — map dev and prod DB names to store codes.
  // Dev DBs:
  if (db === 'DALEEPNEW') return 'NS';
  if (db === 'DALEEP_20260501') return 'MG';
  // Prod DBs (slkatyal_daleepXXX):
  if (db.includes('daleepmdn')) return 'MG';  // Madangir
  if (db.includes('daleepdv'))  return 'DV';  // Devli
  if (db.includes('daleepns'))  return 'NS';  // Neb Sarai
  return db.slice(0, 3);
}

function Th({ children, align = 'left', className }: { children: React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  return <th className={cn('whitespace-nowrap px-2.5 py-2 font-medium', align === 'right' ? 'text-right' : 'text-left', className)}>{children}</th>;
}
function Td({ children, align = 'left', className }: { children: React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  return <td className={cn('whitespace-nowrap px-2.5 py-1.5', align === 'right' ? 'text-right' : 'text-left', className)}>{children}</td>;
}

// Suppress unused warnings for hooks that React DevTools find useful.
void useRouterState;
